import type { Session, User } from "@supabase/supabase-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { supabase } from "../../lib/supabase/client";
import type { MemberProfile } from "./types";

type AuthState = {
  user: User | null;
  session: Session | null;
  profile: MemberProfile | null;
  loading: boolean;
  configured: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(Boolean(supabase));
  const [error, setError] = useState<string | null>(null);
  const profileRequestRef = useRef(0);
  const profileLoadedRef = useRef(false);

  const loadProfile = useCallback(async () => {
    if (!supabase) return;
    const { data, error: profileError } = await supabase.rpc("get_my_profile");
    const currentProfile = (data?.[0] ?? null) as MemberProfile | null;

    if (profileError || !currentProfile) {
      console.error("Unable to load authenticated profile", profileError);
      setError("We could not load your account details. Please try again.");
      profileLoadedRef.current = false;
      setProfile(null);
      return;
    }
    setError(null);
    profileLoadedRef.current = true;
    setProfile(currentProfile);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.user.id) await loadProfile();
  }, [loadProfile, session]);

  useEffect(() => {
    if (!supabase) return;
    let active = true;

    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);
      if (nextSession) {
        // Automatic token renewal should not unmount protected pages or clear
        // an in-progress form. The existing profile remains valid.
        if (event === "TOKEN_REFRESHED") {
          setLoading(false);
          return;
        }

        const requestId = ++profileRequestRef.current;
        if (!profileLoadedRef.current) setLoading(true);

        // Supabase recommends keeping auth-state callbacks synchronous. Defer the
        // profile RPC so the auth client can finish persisting the new session.
        window.setTimeout(() => {
          void loadProfile().finally(() => {
            if (active && profileRequestRef.current === requestId) setLoading(false);
          });
        }, 0);
      } else {
        profileRequestRef.current += 1;
        profileLoadedRef.current = false;
        setProfile(null);
        setError(null);
        setLoading(false);
      }
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, [loadProfile]);

  useEffect(() => {
    if (!session?.user.id) return;
    const interval = window.setInterval(() => {
      void loadProfile();
    }, 30_000);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void loadProfile();
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [loadProfile, session?.user.id]);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user: session?.user ?? null,
      session,
      profile,
      loading,
      configured: Boolean(supabase),
      error,
      refreshProfile,
      signOut,
    }),
    [error, loading, profile, refreshProfile, session, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  return value;
}
